import dayjs from "dayjs";
import {
  FareclassOptionFilter,
  FlightShopStep,
  ITripTerminus,
  Lodging,
  SliceStopCountFilter,
} from "redmond";
import { transformToStringifiedAvailabilityQuery } from "../../availability/utils/queryStringHelpers";
import queryStringParser from "query-string";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_FLIGHT_SHOP,
  PATH_HOME,
  PATH_HOTEL_SHOP,
  PATH_HOTELS_AVAILABILITY,
  PATH_REVIEW,
} from "../../../utils/paths";
import { History } from "history";
import { getSelectedFareClasses } from "../../../utils/fareClass";
export interface IPackageHotelShopParsedQuery {
  lodgingId: string;
  origin: string;
  destination: string;
  fromDate: Date | null;
  untilDate: Date | null;
  adultsCount: number;
  children: number[];
  infants: { age: number; inSeat: boolean }[];
  stopsOption: SliceStopCountFilter;
  fareClassOptionFilter: FareclassOptionFilter;
  searchToken: string;
  placeId: string;
  selectedLodgingIndex?: number;
  shopToken?: string;
  entryPoint?: string;
  isTravelApp?: boolean;
  selectedLodgingIsTopSeller?: boolean;
  selectedLodgingMealPlan?: string;
  selectedLodgingIsSearched?: boolean;
  lodgingSelectedFromMap?: boolean;
  flightShopProgress?: FlightShopStep;
}

export const transformToStringifiedQuery = ({
  lodgingId,
  origin,
  destination,
  fromDate,
  untilDate,
  adultsCount = 2,
  children,
  infants,
  stopsOption,
  fareClassOptionFilter,
  searchToken,
  placeId,
  selectedLodgingIndex,
  shopToken,
  entryPoint,
  isTravelApp,
  selectedLodgingIsSearched,
  selectedLodgingMealPlan,
  selectedLodgingIsTopSeller,
  lodgingSelectedFromMap,
  flightShopProgress,
}: IPackageHotelShopParsedQuery): string => {
  if (fromDate === null || untilDate === null) {
    return `?lodgingId=${lodgingId}`;
  }
  destination = encodeURIComponent(destination);
  const formatFrom = dayjs(fromDate).format("YYYY-MM-DD");
  const formatUntil = dayjs(untilDate).format("YYYY-MM-DD");

  let string = "";
  const childrenParams = children?.reduce(
    (ageString, age) => `${ageString}&children=${age}`,
    ""
  );
  const fareClass = getSelectedFareClasses(fareClassOptionFilter).map(
    (fareClassName) => `&fareClass=${fareClassName}`
  );

  string = `?lodgingId=${lodgingId}&origin=${origin}&destination=${destination}&placeId=${placeId}&fromDate=${formatFrom}&untilDate=${formatUntil}&adultsCount=${adultsCount}${childrenParams}${infants?.reduce(
    (infantString, infant) =>
      `${infantString}&infants=${encodeURIComponent(JSON.stringify(infant))}`,
    ""
  )}&stopsOption=${stopsOption}${fareClass.join("")}${
    selectedLodgingIndex !== undefined
      ? `&selectedLodgingIndex=${selectedLodgingIndex}`
      : ""
  }&searchToken=${searchToken}`;
  if (shopToken) {
    string += `&shopToken=${shopToken}`;
  }
  if (entryPoint) {
    string += `&entryPoint=${entryPoint}`;
  }
  if (isTravelApp) {
    string += `&isTravelApp=${isTravelApp}`;
  }
  if (selectedLodgingIsSearched) {
    string += `&selectedLodgingIsSearched=${selectedLodgingIsSearched}`;
  }
  if (selectedLodgingMealPlan) {
    string += `&selectedLodgingMealPlan=${selectedLodgingMealPlan}`;
  }
  if (selectedLodgingIsTopSeller) {
    string += `&selectedLodgingIsTopSeller=${selectedLodgingIsTopSeller}`;
  }
  if (lodgingSelectedFromMap) {
    string += `&lodgingSelectedFromMap=${lodgingSelectedFromMap}`;
  }
  if (flightShopProgress !== undefined) {
    string += `&flightShopProgress=${flightShopProgress}`;
  }

  return string;
};

export const goToAvailability = ({
  history,
  origin,
  lodging,
  fromDate,
  untilDate,
  adultsCount,
  children,
  infants,
  stopsOption,
  prevSuccessHotelFromDate,
  prevSuccessHotelUntilDate,
  fareClassOptionFilter,
  isTravelApp,
}: {
  history: History;
  origin: ITripTerminus | null;
  lodging: Lodging | null;
  fromDate: Date | string | null;
  untilDate: Date | string | null;
  adultsCount: number;
  children: number[];
  infants: {
    age: number;
    inSeat: boolean;
  }[];
  stopsOption: SliceStopCountFilter;
  prevSuccessHotelFromDate?: Date | null;
  prevSuccessHotelUntilDate?: Date | null;
  fareClassOptionFilter: FareclassOptionFilter;
  isTravelApp?: boolean;
}) => {
  if (
    !lodging ||
    !origin ||
    (!fromDate && !prevSuccessHotelFromDate) ||
    (!untilDate && !prevSuccessHotelUntilDate)
  ) {
    return history.push(PATH_HOME);
  }

  const parsedQueryStringPrimitive = queryStringParser.parse(
    history.location.search
  );

  const lodgingSelection = parsedQueryStringPrimitive.destination as string;
  const placeId = parsedQueryStringPrimitive.placeId as string;
  const search = transformToStringifiedAvailabilityQuery(
    origin?.id?.code?.code,
    lodgingSelection,
    placeId,
    prevSuccessHotelFromDate ?? fromDate!,
    prevSuccessHotelUntilDate ?? untilDate!,
    adultsCount,
    children,
    infants,
    stopsOption,
    fareClassOptionFilter,
    undefined,
    isTravelApp
  );

  history.push(`${PATH_HOTELS_AVAILABILITY}${search}`);
};

export const goToHotelShop = ({ history }: { history: History }) => {
  const params = new URLSearchParams(history.location.search);

  params.delete("selectedFareId");
  params.delete("expandedFlight");
  params.delete("clickedFareId");

  history.push(`${PATH_HOTEL_SHOP}?${params.toString()}`, {
    fromPage: location.pathname,
  });
};

export const goToFlightShop = ({ history }: { history: History }) => {
  const params = new URLSearchParams(history.location.search);

  params.delete("selectedFareId");
  params.delete("expandedFlight");
  params.delete("clickedFareId");

  params.set("flightShopProgress", FlightShopStep.ChooseDeparture.toString());

  history.push(`${PATH_FLIGHT_SHOP}?${params.toString()}`, {
    fromPage: location.pathname,
  });
};

export const goToReview = ({ history }: { history: History }) => {
  const params = new URLSearchParams(history.location.search);

  params.delete("selectedFareId");
  params.delete("expandedFlight");
  params.delete("clickedFareId");

  params.set("flightShopProgress", FlightShopStep.ReviewItinerary.toString());

  history.push(`${PATH_REVIEW}?${params.toString()}`, {
    fromPage: location.pathname,
  });
};

export const goToBook = ({ history }: { history: History }) => {
  const params = new URLSearchParams(history.location.search);

  params.delete("selectedFareId");
  params.delete("expandedFlight");
  params.delete("clickedFareId");

  history.push(`${PATH_BOOK}?${params.toString()}`, {
    fromPage: location.pathname,
  });
};

export const goToBookConfirmation = ({ history }: { history: History }) => {
  history.push(`${PATH_BOOK_CONFIRMATION}${history.location.search}`, {
    fromPage: location.pathname,
  });
};
